/**
 * Hook for diagram mutations (save, delete, duplicate) using React Query
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { defaultConnector } from "../connectors";
import { transformToExternal } from "../transformers";
import { DiagramConnector, DiagramState } from "../types";

interface UseDiagramMutationsOptions {
    /** Custom connector (defaults to environment-based connector) */
    connector?: DiagramConnector;
    /** Callback on successful save */
    onSaveSuccess?: () => void;
    /** Callback on save error */
    onSaveError?: (error: Error) => void;
}

interface UseDiagramMutationsResult {
    /** Save diagram function */
    save: (state: DiagramState) => Promise<void>;
    /** Whether save is in progress */
    isSaving: boolean;
    /** Delete diagram function */
    deleteDiagram: () => Promise<void>;
    /** Whether delete is in progress */
    isDeleting: boolean;
    /** Duplicate diagram function */
    duplicate: (newId: string) => Promise<void>;
    /** Whether duplicate is in progress */
    isDuplicating: boolean;
}

/**
 * Hook for diagram mutations
 *
 * @example
 * ```tsx
 * const { save, isSaving } = useDiagramMutations(integrationId, {
 *     onSaveSuccess: () => toast.success('Saved!'),
 *     onSaveError: (err) => toast.error(err.message),
 * });
 *
 * const handleSave = () => {
 *     const state = diagramRef.current.getState();
 *     save(state);
 * };
 * ```
 */
export const useDiagramMutations = (
    diagramId: string | undefined,
    options: UseDiagramMutationsOptions = {}
): UseDiagramMutationsResult => {
    const { connector = defaultConnector, onSaveSuccess, onSaveError } = options;
    const queryClient = useQueryClient();

    // Save mutation
    const saveMutation = useMutation({
        mutationFn: async (state: DiagramState) => {
            if (!diagramId) {
                throw new Error("Diagram ID is required");
            }
            const externalData = transformToExternal(state);
            await connector.save(diagramId, externalData);
        },
        onSuccess: () => {
            // Note: We don't invalidate/refetch here because:
            // 1. Local diagram state is preserved (not reset from props)
            // 2. beIds are synced from PATCH response in connector.save()
            // 3. Extra GET would be wasted since we don't apply it
            onSaveSuccess?.();
        },
        onError: (error: Error) => {
            console.error("Failed to save diagram:", error);
            onSaveError?.(error);
        },
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: async () => {
            if (!diagramId) {
                throw new Error("Diagram ID is required");
            }
            if (!connector.delete) {
                throw new Error("Delete not supported by this connector");
            }
            await connector.delete(diagramId);
        },
        onSuccess: () => {
            // Remove from cache
            queryClient.removeQueries({ queryKey: ["cc-diagram", diagramId] });
        },
    });

    // Duplicate mutation
    const duplicateMutation = useMutation({
        mutationFn: async (newId: string) => {
            if (!diagramId) {
                throw new Error("Diagram ID is required");
            }
            if (!connector.duplicate) {
                throw new Error("Duplicate not supported by this connector");
            }
            await connector.duplicate(diagramId, newId);
        },
        onSuccess: (_data, newId) => {
            // Invalidate to fetch the new diagram
            queryClient.invalidateQueries({ queryKey: ["cc-diagram", newId] });
        },
    });

    return {
        save: saveMutation.mutateAsync,
        isSaving: saveMutation.isPending,
        deleteDiagram: deleteMutation.mutateAsync,
        isDeleting: deleteMutation.isPending,
        duplicate: duplicateMutation.mutateAsync,
        isDuplicating: duplicateMutation.isPending,
    };
};
